import { useEffect, useRef } from 'react';
import { useClickAway, useKeyPressEvent, useLockBodyScroll } from 'react-use';
import { useTranslation } from 'react-i18next';

import { Portal } from 'components/Portal';

import * as S from './SupportTicketModal.styles';

type SupportTicketModalProps = {
  children: React.ReactNode;
  onClose: () => void;
};

// Scoped, local duplicate of components/Modal/Modal.tsx's behavior — kept
// out of the shared Modal component on purpose, so nothing here (the
// scrollbar-gutter compensation below, the bigger close button in
// SupportTicketModal.styles.ts) affects any other modal in the app.
//
// react-use's `useLockBodyScroll` only sets `overflow: hidden` on <body> — it
// doesn't compensate for the scrollbar it removes, which reflows body
// content and visually shifts any `position: fixed` element anchored via
// `right` (e.g. SupportTicketWidget's own floating button). Counter-based
// (not a plain flag) so this survives if this modal is ever nested inside
// another one later.
let scrollLockCount = 0;

// Real browser scrollbars never exceed ~40px — this upper bound guards
// against environments without a real layout engine (e.g. jsdom in tests,
// where `document.documentElement.clientWidth` is always 0), which would
// otherwise produce a huge, meaningless "scrollbar width".
const MAX_PLAUSIBLE_SCROLLBAR_WIDTH = 40;

function lockScrollbarGutter() {
  if (scrollLockCount === 0) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0 && scrollbarWidth <= MAX_PLAUSIBLE_SCROLLBAR_WIDTH) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      // position: fixed elements anchor to the viewport, not to <body> —
      // padding-right above doesn't reach them. Exposed as a CSS var so
      // SupportTicketWidget.styles.ts's FloatingButton can add it to its own
      // `right` value.
      document.documentElement.style.setProperty(
        '--support-ticket-scroll-lock-offset',
        `${scrollbarWidth}px`
      );
    }
  }
  scrollLockCount += 1;
}

function unlockScrollbarGutter() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--support-ticket-scroll-lock-offset');
  }
}

export function SupportTicketModal(props: SupportTicketModalProps): React.ReactElement {
  const { children, onClose } = props;
  const { t } = useTranslation();

  const ref = useRef(null);

  useClickAway(ref, onClose);
  useKeyPressEvent('Escape', onClose);

  // Must run BEFORE `useLockBodyScroll` — effects fire in declaration order,
  // and this measures the scrollbar's width, which only exists before
  // `useLockBodyScroll`'s own effect sets `overflow: hidden` and removes it.
  useEffect(() => {
    lockScrollbarGutter();

    return unlockScrollbarGutter;
  }, []);

  useLockBodyScroll(true);

  return (
    <Portal>
      <S.Container onClick={(e) => e.stopPropagation()}>
        <S.Box ref={ref}>
          <S.CloseButton
            iconLeft="FiX"
            onClick={onClose}
            aria-label={t('common.close')}
            testID="support-ticket-modal-close-button"
          />

          <S.BoxInner>{children}</S.BoxInner>
        </S.Box>
      </S.Container>
    </Portal>
  );
}
